/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { ReactNode, createContext, useContext } from 'react';
import mapValues from 'lodash/mapValues';
import { useAlertReducer, alertActions, AlertDispatch, Alert } from './modules/alerts';
import { useUserReducer, userActions, UserDispatch, User } from './modules/user';
import {
  useAppSettingsReducer,
  appSettingsActions,
  AppSettingsDispatch,
  AppSettings,
} from './modules/appSettings';

const initialState: Record<string, unknown> = {};
const Store = createContext(initialState);

interface StoreProviderProps {
  children: ReactNode;
}

type Action<Dispatch, State> = (dispatch: Dispatch, state: State) => void;

type StoreType = {
  appSettings: {
    state: AppSettings;
    actions: typeof appSettingsActions;
  };
  alert: {
    state: Alert;
    actions: typeof alertActions;
  };
  user: {
    state: User;
    actions: typeof userActions;
  };
};

export const StoreProvider = ({ children }: StoreProviderProps): JSX.Element => {
  const [stateAppSettings, dispatchAppSettings] = useAppSettingsReducer();
  const [stateAlert, dispatchAlert] = useAlertReducer();
  const [stateUser, dispatchUser] = useUserReducer();
  const store = React.useMemo(
    () => ({
      appSettings: {
        state: stateAppSettings,
        actions: mapValues(
          appSettingsActions,
          (action: (...args: any) => Action<AppSettingsDispatch, AppSettings>) => (...args: any) => action(...args)(dispatchAppSettings, stateAppSettings),
        ),
      },
      alert: {
        state: stateAlert,
        actions: mapValues(
          alertActions,
          (action: (...args: any) => Action<AlertDispatch, Alert>) => (...args: any) => action(...args)(dispatchAlert, stateAlert),
        ),
      },
      user: {
        state: stateUser,
        actions: mapValues(
          userActions,
          (action: (...args: any) => Action<UserDispatch, User>) => (...args: any) => action(...args)(dispatchUser, stateUser),
        ),
      },
    }),
    [stateAppSettings, dispatchAppSettings, stateAlert, dispatchAlert, stateUser, dispatchUser],
  );

  return <Store.Provider value={store}>{children}</Store.Provider>;
};

export const useStore = (): StoreType => useContext(Store) as StoreType;
